import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Modal } from '../Modal';
import { Button } from '../design-system';
import { apiService, DataList, DataListRecord } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

interface DataListRecordPickerProps {
  visible: boolean;
  onClose: () => void;
  targetListId: string;
  onSelect: (recordId: string, label: string) => void;
  /** Many-to-many/one-to-many mode — tapping toggles a checkmark instead of
   * immediately selecting+closing; a Done button commits the full set. */
  multiple?: boolean;
  /** multiple only — ids already selected, so they render checked on open. */
  selectedIds?: string[];
  /** multiple only — commits the final selection when Done is pressed. */
  onSelectMultiple?: (recordIds: string[], labels: Record<string, string>) => void;
}

/** Search-select modal for a `relation` column — structurally like BusinessUserPicker but
 * backed by apiService.getDataListRecords(targetListId, { search }). Displays each target
 * record's first text-column value as a human-readable label. */
export const DataListRecordPicker: React.FC<DataListRecordPickerProps> = ({
  visible,
  onClose,
  targetListId,
  onSelect,
  multiple = false,
  selectedIds,
  onSelectMultiple,
}) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [records, setRecords] = useState<DataListRecord[]>([]);
  const [targetList, setTargetList] = useState<DataList | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const [pendingLabels, setPendingLabels] = useState<Record<string, string>>({});

  const firstTextColumnId = React.useMemo(() => {
    const col = targetList?.columns
      ?.slice()
      .sort((a, b) => a.order - b.order)
      .find((c) => c.type === 'text');
    return col?.id;
  }, [targetList]);

  const labelForRecord = useCallback(
    (record: DataListRecord): string => {
      if (firstTextColumnId && record.values?.[firstTextColumnId] != null) {
        return String(record.values[firstTextColumnId]);
      }
      // Fall back to the first non-empty value, then the id, so a record always shows something.
      const firstValue = Object.values(record.values || {}).find((v) => v != null && v !== '');
      return firstValue != null ? String(firstValue) : t('dataLists.recordPicker.unnamed');
    },
    [firstTextColumnId, t]
  );

  const loadList = useCallback(async () => {
    try {
      const res = await apiService.getDataList(targetListId);
      if (res.statusCode === 1000 && res.data) {
        setTargetList(res.data);
      }
    } catch (error) {
      console.error('[DataListRecordPicker] Failed to load target list:', error);
    }
  }, [targetListId]);

  const loadRecords = useCallback(async () => {
    if (!targetListId) return;
    try {
      setLoading(true);
      const res = await apiService.getDataListRecords(targetListId, {
        search: searchQuery.trim() || undefined,
        limit: 50,
      });
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        setRecords(res.data);
      } else {
        setRecords([]);
      }
    } catch (error) {
      console.error('[DataListRecordPicker] Failed to load records:', error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [targetListId, searchQuery]);

  useEffect(() => {
    if (visible) {
      setSearchQuery('');
      loadList();
      if (multiple) {
        setPendingIds(selectedIds || []);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, targetListId]);

  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => {
      loadRecords();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, searchQuery, targetListId]);

  const toggleMultiple = (item: DataListRecord, label: string) => {
    setPendingLabels((prev) => ({ ...prev, [item._id]: label }));
    setPendingIds((prev) => (prev.includes(item._id) ? prev.filter((id) => id !== item._id) : [...prev, item._id]));
  };

  const renderItem = ({ item }: { item: DataListRecord }) => {
    const label = labelForRecord(item);
    const checked = multiple && pendingIds.includes(item._id);
    return (
      <TouchableOpacity
        style={[styles.item, { flexDirection: rtl.rowDirection }]}
        onPress={() => {
          if (multiple) {
            toggleMultiple(item, label);
            return;
          }
          onSelect(item._id, label);
          onClose();
        }}
        activeOpacity={0.7}
      >
        <Ionicons name={checked ? 'checkmark-circle' : 'document-text-outline'} size={20} color={colors.primary} />
        <Text style={[styles.itemLabel, { textAlign: rtl.textStart }]} numberOfLines={1}>
          {label}
        </Text>
      </TouchableOpacity>
    );
  };

  return (
    <Modal visible={visible} onClose={onClose} title={t('dataLists.recordPicker.title')} size="large" scrollable={false}>
      <View style={styles.content}>
        <View style={[styles.searchContainer, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name="search" size={20} color={colors.textLight} style={rtl.marginEnd(spacing.xs)} />
          <TextInput
            placeholder={t('dataLists.recordPicker.searchPlaceholder')}
            value={searchQuery}
            onChangeText={setSearchQuery}
            style={[styles.searchInput, { textAlign: rtl.textStart }]}
            placeholderTextColor={colors.textLight as string}
          />
        </View>

        {loading && records.length === 0 ? (
          <View style={styles.centerContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={styles.loadingText}>{t('dataLists.recordPicker.loading')}</Text>
          </View>
        ) : records.length === 0 ? (
          <View style={styles.centerContainer}>
            <Ionicons name="documents-outline" size={56} color={colors.textLight} />
            <Text style={styles.emptyText}>{t('dataLists.recordPicker.empty')}</Text>
          </View>
        ) : (
          <FlatList
            data={records}
            renderItem={renderItem}
            keyExtractor={(item) => item._id}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
          />
        )}
        {multiple && (
          <View style={styles.doneRow}>
            <Button
              title={t('dataLists.recordPicker.done', { count: pendingIds.length })}
              onPress={() => {
                onSelectMultiple?.(pendingIds, pendingLabels);
                onClose();
              }}
            />
          </View>
        )}
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  content: {
    flex: 1,
    minHeight: 360,
  },
  doneRow: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    margin: spacing.md,
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
    paddingVertical: 0,
  },
  listContent: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    marginBottom: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
    ...designTokens.shadows.sm,
  },
  itemLabel: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.medium,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
  },
  emptyText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
  },
});
